import { useMemo, useRef, useState } from 'react';
import { Drawer, FormField, TextInput, Select } from '@/components/ui';
import { Icon, type IconName } from '@/components/ui/Icon';
import { toast } from '@/components/ui/Toast';
import {
  adminApi,
  type HubAutomation,
  type HubAutomationInput,
  type HubCatalog,
  type HubCatalogWebhook,
  type HubCondition,
  type HubConditionGroups,
  type HubAction,
  type HubTriggerType,
} from '@/shared/api/adminApi';
import { CopyField } from './common';
import { ActionConfigForm } from './actionForms';
import { GENERAL_VARS, varsForEvents, type Var } from './eventVars';
import { VarChip } from './VarChip';
import { ACTION_META, TRIGGER_ICON, toGroups, emptyRow, enumForField, actionLock } from './automationMeta';

export interface BuilderPrefill {
  trigger_type?: HubTriggerType;
  trigger_config?: Record<string, unknown>;
}

// עוקבים אחרי איזה שדה (בכל פעולה) היה אחרון בפוקוס, כדי שלחיצה על משתנה
// תזריק אותו לשדה הפעיל במיקום הסמן.
type ActiveField = { actionIndex: number; key: string } | null;

export function AutomationBuilder({
  automation,
  prefill,
  catalog,
  onClose,
  onSaved,
}: {
  automation: HubAutomation | null;
  prefill?: BuilderPrefill;
  catalog: HubCatalog;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!automation;
  const [name, setName] = useState(automation?.name ?? '');
  const [triggerType, setTriggerType] = useState<HubTriggerType>(automation?.trigger_type ?? prefill?.trigger_type ?? 'event');
  const [triggerConfig, setTriggerConfig] = useState<Record<string, unknown>>(automation?.trigger_config ?? prefill?.trigger_config ?? {});
  const [groups, setGroups] = useState<HubConditionGroups>(toGroups(automation?.conditions));
  const [actions, setActions] = useState<HubAction[]>(automation?.actions ?? []);
  const [saving, setSaving] = useState(false);

  const caps = catalog.capabilities;
  const eventOpts = Object.entries(catalog.events).map(([value, label]) => ({ value, label }));
  // פעולות נעולות (חסרת יכולת) מסומנות במנעול ולא ניתנות להוספה.
  const actionOpts = Object.entries(catalog.actions).map(([value, label]) => ({
    value, label: actionLock(value, caps) ? `🔒 ${label}` : label,
  }));
  const operators = catalog.operators.length ? catalog.operators : ['=', '!=', '>', '<', 'contains', 'in', 'exists', 'not_exists'];

  const selectedEvent = typeof triggerConfig.event === 'string' ? triggerConfig.event : '';
  const inboundUrl = typeof triggerConfig.url === 'string' ? triggerConfig.url : '';

  // המשתנים הזמינים להזרקה — מסוננים לפי האירוע שנבחר (אם יש).
  const availableVars = useMemo<Var[]>(() => {
    const evs = selectedEvent && selectedEvent !== '*' ? [selectedEvent] : [];
    return [...GENERAL_VARS, ...varsForEvents(evs)];
  }, [selectedEvent]);

  // מעקב שדה פעיל + refs לאלמנטים, להזרקת משתנה במיקום הסמן.
  const activeField = useRef<ActiveField>(null);
  const fieldEls = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({});
  const registerField = (actionIndex: number, key: string) => (el: HTMLInputElement | HTMLTextAreaElement | null) => {
    fieldEls.current[`${actionIndex}:${key}`] = el;
  };
  const onFieldFocus = (actionIndex: number, key: string) => () => { activeField.current = { actionIndex, key }; };

  function setTC(k: string, v: unknown) { setTriggerConfig((c) => ({ ...c, [k]: v })); }

  // ── תנאים (קבוצות OR של שורות AND) ─────────────────────────────────────────
  function setMatch(match: 'all' | 'any') { setGroups((g) => ({ ...g, match })); }
  function addGroup() { setGroups((g) => ({ ...g, groups: [...g.groups, [emptyRow(operators[0])]] })); }
  function removeGroup(gi: number) { setGroups((g) => ({ ...g, groups: g.groups.filter((_, j) => j !== gi) })); }
  function addRow(gi: number) {
    setGroups((g) => ({ ...g, groups: g.groups.map((grp, j) => (j === gi ? [...grp, emptyRow(operators[0])] : grp)) }));
  }
  function updateRow(gi: number, ri: number, patch: Partial<HubCondition>) {
    setGroups((g) => ({ ...g, groups: g.groups.map((grp, j) => (j === gi ? grp.map((r, k) => (k === ri ? { ...r, ...patch } : r)) : grp)) }));
  }
  function removeRow(gi: number, ri: number) {
    setGroups((g) => {
      const next = g.groups.map((grp, j) => (j === gi ? grp.filter((_, k) => k !== ri) : grp)).filter((grp) => grp.length > 0);
      return { ...g, groups: next };
    });
  }

  // ── פעולות ──────────────────────────────────────────────────────────────────
  function addAction(type: string) {
    if (!type) return;
    const lock = actionLock(type, caps);
    if (lock) { toast(lock, 'warn'); return; } // פעולה נעולה — לא ניתן להוסיף
    setActions((a) => [...a, { type, config: {} }]);
  }
  function updateActionConfig(i: number, config: Record<string, unknown>) {
    setActions((a) => a.map((row, j) => (j === i ? { ...row, config } : row)));
  }
  function removeAction(i: number) { setActions((a) => a.filter((_, j) => j !== i)); }
  function moveAction(i: number, dir: -1 | 1) {
    setActions((a) => {
      const j = i + dir;
      if (j < 0 || j >= a.length) return a;
      const next = [...a];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  // הזרקת טוקן {{...}} לשדה הפעיל האחרון (או append לסוף אם אין).
  function insertVar(token: string) {
    const f = activeField.current;
    if (!f) { toast('לחץ קודם בשדה טקסט בתוך פעולה', 'warn'); return; }
    const el = fieldEls.current[`${f.actionIndex}:${f.key}`];
    const current = String((actions[f.actionIndex]?.config as Record<string, unknown>)?.[f.key] ?? '');
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);
    updateActionConfig(f.actionIndex, { ...actions[f.actionIndex].config, [f.key]: next });
    requestAnimationFrame(() => {
      const node = fieldEls.current[`${f.actionIndex}:${f.key}`];
      if (node) { node.focus(); const pos = start + token.length; node.setSelectionRange(pos, pos); }
    });
  }

  async function save() {
    if (!name.trim()) { toast('יש להזין שם', 'danger'); return; }
    if (actions.length === 0) { toast('הוסף לפחות פעולה אחת', 'danger'); return; }
    if (triggerType === 'event' && !triggerConfig.event) { toast('בחר אירוע לטריגר', 'danger'); return; }
    if (triggerType === 'schedule' && !triggerConfig.cron) { toast('הזן ביטוי cron', 'danger'); return; }

    // ניקוי שורות ריקות; קבוצה שנשארה ריקה מושמטת.
    const cleanGroups = groups.groups
      .map((grp) => grp.filter((r) => r.field.trim()))
      .filter((grp) => grp.length > 0);
    const conditions = cleanGroups.length ? { match: groups.match, groups: cleanGroups } : [];

    const payload: HubAutomationInput = {
      name: name.trim(),
      trigger_type: triggerType,
      trigger_config: triggerConfig,
      conditions,
      actions,
    };
    setSaving(true);
    try {
      if (automation) {
        await adminApi.updateHubAutomation(automation.id, {
          name: payload.name,
          trigger_config: payload.trigger_config,
          conditions: payload.conditions,
          actions: payload.actions,
        });
        toast('נשמר', 'ok');
      } else {
        await adminApi.createHubAutomation(payload);
        toast('נוצר', 'ok');
      }
      onSaved();
    } catch (e: unknown) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  const totalRows = groups.groups.reduce((s, g) => s + g.length, 0);

  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={isEdit ? 'עריכת אוטומציה' : 'אוטומציה חדשה'}
      width={680}
      footer={<>
        <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
        <button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמור אוטומציה'}</button>
      </>}
    >
      <div className="stack" style={{ gap: 18 }}>
        <FormField label="שם האוטומציה" required>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="למשל: שיחה שלא נענתה → WhatsApp" />
        </FormField>

        {/* ── סעיף 1: מתי? (טריגר) ─────────────────────────────────────────── */}
        <StepBlock n={1} icon={TRIGGER_ICON[triggerType] ?? 'bolt'} title="מתי זה ירוץ?" subtitle="הטריגר שמפעיל את האוטומציה">
          <FormField label="סוג טריגר">
            <Select
              value={triggerType}
              onChange={(v) => { if (!isEdit) { setTriggerType(v as HubTriggerType); setTriggerConfig({}); } }}
              options={[
                { value: 'event', label: 'אירוע במערכת' },
                { value: 'inbound', label: 'כתובת נכנסת (Webhook חיצוני)' },
                { value: 'schedule', label: 'מתוזמן (cron)' },
              ]}
            />
          </FormField>
          {isEdit && <Hint>לא ניתן לשנות את סוג הטריגר לאחר יצירה.</Hint>}

          {triggerType === 'event' && (
            <FormField label="בחר אירוע">
              <Select
                value={selectedEvent}
                onChange={(v) => setTC('event', v)}
                options={[{ value: '', label: 'בחר אירוע…' }, { value: '*', label: '★ כל האירועים' }, ...eventOpts]}
              />
            </FormField>
          )}
          {triggerType === 'schedule' && (
            <FormField label="ביטוי cron" hint="דקה שעה יום חודש יום-בשבוע · למשל 0 9 * * * = כל יום ב-9:00">
              <TextInput value={typeof triggerConfig.cron === 'string' ? triggerConfig.cron : ''} onChange={(e) => setTC('cron', e.target.value)} dir="ltr" placeholder="0 9 * * *" />
            </FormField>
          )}
          {triggerType === 'inbound' && (
            inboundUrl ? (
              <FormField label="כתובת קליטה (שלח לכאן POST כדי להפעיל)">
                <CopyField value={inboundUrl} />
              </FormField>
            ) : (
              <Hint>כתובת הקליטה תיווצר ותוצג לאחר השמירה. כל שדה שיישלח ב-POST יהיה זמin כ-<code dir="ltr">{'{{data.שדה}}'}</code>.</Hint>
            )
          )}
        </StepBlock>

        {/* ── סעיף 2: רק אם? (תנאים) ────────────────────────────────────────── */}
        <StepBlock n={2} icon="filter" title="רק אם…" subtitle="תנאים שחייבים להתקיים (אופציונלי)">
          {groups.groups.length === 0 ? (
            <div className="stack" style={{ gap: 8 }}>
              <Hint>ללא תנאים — האוטומציה תרוץ בכל פעם שהטריגר קורה.</Hint>
              <div><button type="button" className="btn ghost" onClick={addGroup}><Icon name="plus" size={13} /> הוסף תנאי</button></div>
            </div>
          ) : (
            <div className="stack" style={{ gap: 10 }}>
              {groups.groups.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>בין הקבוצות:</span>
                  <div style={{ minWidth: 160 }}>
                    <Select value={groups.match} onChange={(v) => setMatch(v as 'all' | 'any')}
                      options={[{ value: 'all', label: 'כל הקבוצות (AND)' }, { value: 'any', label: 'לפחות קבוצה אחת (OR)' }]} />
                  </div>
                </div>
              )}
              {groups.groups.map((grp, gi) => (
                <div key={gi}>
                  {gi > 0 && (
                    <div style={{ textAlign: 'center', margin: '2px 0' }}>
                      <span style={{ fontSize: 'var(--t-xs)', fontWeight: 700, color: 'var(--accent)', padding: '2px 10px', background: 'var(--accent-soft, var(--surface-2))', borderRadius: 'var(--r-pill)' }}>
                        {groups.match === 'any' ? 'או' : 'וגם'}
                      </span>
                    </div>
                  )}
                  <ConditionGroup
                    rows={grp}
                    operators={operators}
                    vars={availableVars}
                    event={selectedEvent}
                    onUpdateRow={(ri, patch) => updateRow(gi, ri, patch)}
                    onAddRow={() => addRow(gi)}
                    onRemoveRow={(ri) => removeRow(gi, ri)}
                    onRemoveGroup={() => removeGroup(gi)}
                  />
                </div>
              ))}
              <div><button type="button" className="btn ghost" onClick={addGroup}><Icon name="plus" size={13} /> {groups.match === 'any' ? 'הוסף קבוצת OR' : 'הוסף קבוצה'}</button></div>
            </div>
          )}
        </StepBlock>

        {/* ── סעיף 3: אז? (פעולות) ──────────────────────────────────────────── */}
        <StepBlock n={3} icon="bolt" title="אז תעשה…" subtitle="הפעולות שירוצו לפי הסדר">
          {actions.length === 0 && <Hint>הוסף פעולה אחת או יותר.</Hint>}

          {/* בנק משתנים לחיצים — משותף לכל הפעולות */}
          {actions.length > 0 && (
            <div style={{ padding: 10, border: '1px dashed var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface-2)' }}>
              <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 6 }}>
                לחץ בשדה טקסט של פעולה ואז על משתנה כדי להזריק אותו:
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {availableVars.map((v) => <VarChip key={v.token} v={v} onInsert={insertVar} />)}
              </div>
            </div>
          )}

          {actions.map((a, i) => (
            <ActionCard
              key={i}
              index={i}
              total={actions.length}
              action={a}
              label={catalog.actions[a.type] ?? a.type}
              lock={actionLock(a.type, caps)}
              onConfig={(cfg) => updateActionConfig(i, cfg)}
              onMove={(dir) => moveAction(i, dir)}
              onRemove={() => removeAction(i)}
              webhooks={catalog.webhooks}
              registerField={registerField}
              onFieldFocus={onFieldFocus}
            />
          ))}

          <div style={{ maxWidth: 260 }}>
            <Select value="" onChange={(v) => addAction(v)} options={[{ value: '', label: '+ הוסף פעולה…' }, ...actionOpts]} />
          </div>
        </StepBlock>

        {/* ── תצוגה מקדימה של ה-flow ────────────────────────────────────────── */}
        <FlowPreview
          triggerLabel={triggerType === 'event' ? (selectedEvent ? (catalog.events[selectedEvent] ?? (selectedEvent === '*' ? 'כל האירועים' : selectedEvent)) : 'אירוע לא נבחר') : triggerType === 'schedule' ? 'מתוזמן' : 'כתובת נכנסת'}
          triggerIcon={TRIGGER_ICON[triggerType] ?? 'bolt'}
          match={groups.match}
          rowCount={totalRows}
          groupCount={groups.groups.length}
          actions={actions.map((a) => ({
            label: catalog.actions[a.type] ?? a.type,
            icon: ACTION_META[a.type]?.icon ?? 'bolt',
            delay: Number((a.config as { delay?: unknown })?.delay ?? 0),
          }))}
        />
      </div>
    </Drawer>
  );
}

// ── קבוצת תנאים אחת (שורות AND) ───────────────────────────────────────────────
function ConditionGroup({
  rows, operators, vars, event, onUpdateRow, onAddRow, onRemoveRow, onRemoveGroup,
}: {
  rows: HubCondition[];
  operators: string[];
  vars: Var[];
  event: string;
  onUpdateRow: (ri: number, patch: Partial<HubCondition>) => void;
  onAddRow: () => void;
  onRemoveRow: (ri: number) => void;
  onRemoveGroup: () => void;
}) {
  const fieldOpts = [{ value: '', label: 'בחר שדה…' }, ...vars.map((v) => ({ value: v.token.replace(/^\{\{|\}\}$/g, ''), label: v.label }))];
  const opLabels: Record<string, string> = {
    '=': 'שווה', '!=': 'שונה', '>': 'גדול מ', '<': 'קטן מ', '>=': 'גדול/שווה', '<=': 'קטן/שווה',
    contains: 'מכיל', not_contains: 'לא מכיל', starts_with: 'מתחיל ב', in: 'אחד מ (מופרד בפסיק)',
    exists: 'קיים', not_exists: 'לא קיים',
  };
  const noValueOps = new Set(['exists', 'not_exists']);

  // בשינוי שדה: אם השדה החדש הוא enum והערך הישן אינו אחת מהאפשרויות — נאפס אותו.
  const changeField = (field: string, oldValue: string): Partial<HubCondition> => {
    const enumOpts = enumForField(field, event);
    const valueStillValid = !enumOpts || enumOpts.some((o) => o.value === oldValue);
    return valueStillValid ? { field } : { field, value: '' };
  };

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 10, background: 'var(--surface)' }}>
      <div className="stack" style={{ gap: 6 }}>
        {rows.map((r, ri) => {
          const known = fieldOpts.some((o) => o.value === r.field);
          return (
            <div key={ri} className="stack" style={{ gap: 4 }}>
              {ri > 0 && <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', paddingInlineStart: 4 }}>וגם</div>}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 150px', minWidth: 130 }}>
                  {/* בורר שדה מוכר, עם נפילה לשדה חופשי לערכים ידניים/מקוננים.
                      בשינוי שדה לאנום — מנקים ערך שלא תקף עוד. */}
                  {known || !r.field ? (
                    <Select value={r.field} onChange={(v) => onUpdateRow(ri, changeField(v, r.value))} options={fieldOpts} />
                  ) : (
                    <TextInput value={r.field} onChange={(e) => onUpdateRow(ri, changeField(e.target.value, r.value))} dir="ltr" placeholder="data.field" />
                  )}
                </div>
                <div style={{ minWidth: 120 }}>
                  <Select value={r.op} onChange={(v) => onUpdateRow(ri, { op: v })}
                    options={operators.map((op) => ({ value: op, label: opLabels[op] ?? op }))} />
                </div>
                {!noValueOps.has(r.op) && (() => {
                  const enumOpts = enumForField(r.field, event);
                  // שדה עם ערכים ידועים (סטטוס/כיוון/סנטימנט/עדיפות) → בורר ערך.
                  // עבור 'in' (רב-ערכי, מופרד בפסיק) נשאר טקסט חופשי.
                  return (
                    <div style={{ flex: '1 1 120px', minWidth: 110 }}>
                      {enumOpts && r.op !== 'in' ? (
                        <Select
                          value={r.value}
                          onChange={(v) => onUpdateRow(ri, { value: v })}
                          options={[{ value: '', label: 'בחר ערך…' }, ...enumOpts]}
                        />
                      ) : (
                        <TextInput
                          value={r.value}
                          onChange={(e) => onUpdateRow(ri, { value: e.target.value })}
                          placeholder={enumOpts ? 'ערכים מופרדים בפסיק' : 'ערך'}
                        />
                      )}
                    </div>
                  );
                })()}
                <button type="button" className="btn ghost" onClick={() => onRemoveRow(ri)} aria-label="הסר תנאי" title="הסר תנאי">
                  <Icon name="close" size={13} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        <button type="button" className="btn ghost" onClick={onAddRow} style={{ fontSize: 'var(--t-xs)' }}><Icon name="plus" size={12} /> וגם תנאי</button>
        <button type="button" className="btn ghost" onClick={onRemoveGroup} style={{ fontSize: 'var(--t-xs)', color: 'var(--danger)' }}><Icon name="trash" size={12} /> הסר קבוצה</button>
      </div>
    </div>
  );
}

// ── כרטיס פעולה בודדת ─────────────────────────────────────────────────────────
function ActionCard({
  index, total, action, label, lock, onConfig, onMove, onRemove, registerField, onFieldFocus, webhooks,
}: {
  index: number;
  total: number;
  action: HubAction;
  label: string;
  lock: string | null;
  webhooks?: HubCatalogWebhook[];
  onConfig: (cfg: Record<string, unknown>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  registerField: (actionIndex: number, key: string) => (el: HTMLInputElement | HTMLTextAreaElement | null) => void;
  onFieldFocus: (actionIndex: number, key: string) => () => void;
}) {
  const meta = ACTION_META[action.type];
  const delay = Number((action.config as { delay?: unknown })?.delay ?? 0);
  const setDelay = (v: string) => {
    const n = v === '' ? undefined : Math.max(0, Number(v));
    const cfg = { ...action.config };
    if (n && n > 0) cfg.delay = n; else delete cfg.delay;
    onConfig(cfg);
  };

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)' }}>
        <span style={{ display: 'inline-flex', width: 22, height: 22, borderRadius: 'var(--r-1, 6px)', background: lock ? 'var(--surface)' : 'var(--accent-soft, var(--surface))', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={lock ? 'lock' : (meta?.icon ?? 'bolt')} size={13} color={lock ? 'var(--warn)' : 'var(--accent)'} />
        </span>
        <strong style={{ fontSize: 'var(--t-sm)', flex: 1, color: lock ? 'var(--muted)' : undefined }}>{index + 1}. {label}</strong>
        {delay > 0 && (
          <span title="פעולה מושהית" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
            <Icon name="clock" size={12} /> {delay} דק׳
          </span>
        )}
        <div style={{ display: 'flex', gap: 2 }}>
          <button type="button" className="btn ghost" onClick={() => onMove(-1)} disabled={index === 0} aria-label="למעלה"><Icon name="arrowR" size={12} /></button>
          <button type="button" className="btn ghost" onClick={() => onMove(1)} disabled={index === total - 1} aria-label="למטה"><Icon name="arrowL" size={12} /></button>
          <button type="button" className="btn ghost" onClick={onRemove} aria-label="הסר"><Icon name="trash" size={12} /></button>
        </div>
      </div>
      <div className="stack" style={{ padding: 'var(--pad-card)', gap: 10 }}>
        {/* פעולה נעולה: חוסמת עריכה עם הודעת-נעילה בולטת. שאר הפעולות רגילות. */}
        {lock && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 'var(--t-sm)', color: 'var(--warn)', background: 'var(--surface-2)', border: '1px solid var(--warn, var(--line))', borderRadius: 'var(--r-2)', padding: '8px 10px' }}>
            <Icon name="lock" size={15} color="var(--warn)" />
            <span>{lock}</span>
          </div>
        )}
        {meta?.hint && <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{meta.hint}</div>}
        {!lock && meta?.warn && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 'var(--t-xs)', color: 'var(--warn)', background: 'var(--surface-2)', border: '1px solid var(--warn, var(--line))', borderRadius: 'var(--r-2)', padding: '6px 8px' }}>
            <Icon name="warn" size={13} color="var(--warn)" />
            <span>{meta.warn}</span>
          </div>
        )}
        <ActionConfigForm
          type={action.type}
          config={action.config}
          onChange={onConfig}
          fieldReg={(key) => ({ ref: registerField(index, key), onFocus: onFieldFocus(index, key) })}
          webhooks={webhooks}
        />
        {/* השהיה פר-פעולה — גנרי לכל סוג */}
        <FormField label="השהיית הרצה (דקות)" hint="0 = מיידי. אחרת הפעולה תרוץ בעוד X דקות (למשל SMS מעקב אחרי 10 דק׳).">
          <TextInput type="number" value={delay ? String(delay) : ''} onChange={(e) => setDelay(e.target.value)} dir="ltr" placeholder="0" style={{ maxWidth: 140 }} />
        </FormField>
      </div>
    </div>
  );
}

// ── תצוגה מקדימה חיה של ה-flow ────────────────────────────────────────────────
function FlowPreview({
  triggerLabel, triggerIcon, match, rowCount, groupCount, actions,
}: {
  triggerLabel: string;
  triggerIcon: IconName;
  match: 'all' | 'any';
  rowCount: number;
  groupCount: number;
  actions: { label: string; icon: IconName; delay: number }[];
}) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 'var(--pad-card)', background: 'var(--surface-2)' }}>
      <div style={{ fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--muted)', marginBottom: 8 }}>תצוגה מקדימה</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 'var(--t-xs)' }}>
        <PreviewPill icon={triggerIcon} tone="accent">{triggerLabel}</PreviewPill>
        <Icon name="arrowL" size={12} color="var(--muted)" />
        {rowCount > 0 && (
          <>
            <PreviewPill icon="filter" tone="neutral">{rowCount} תנאים{groupCount > 1 ? ` (${match === 'any' ? 'OR' : 'AND'})` : ''}</PreviewPill>
            <Icon name="arrowL" size={12} color="var(--muted)" />
          </>
        )}
        {actions.length === 0 ? (
          <PreviewPill icon="warn" tone="warn">ללא פעולות</PreviewPill>
        ) : (
          actions.map((ac, i) => (
            <PreviewPill key={i} icon={ac.icon} tone="neutral" clock={ac.delay > 0 ? ac.delay : undefined}>{ac.label}</PreviewPill>
          ))
        )}
      </div>
    </div>
  );
}

function PreviewPill({ icon, tone, children, clock }: { icon: IconName; tone: 'accent' | 'neutral' | 'warn'; children: React.ReactNode; clock?: number }) {
  const fg = tone === 'accent' ? 'var(--accent)' : tone === 'warn' ? 'var(--warn)' : 'var(--text-2, var(--muted))';
  const bg = tone === 'accent' ? 'var(--accent-soft, var(--surface))' : 'var(--surface)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 'var(--r-pill)', background: bg, color: fg, border: '1px solid var(--line)' }}>
      <Icon name={icon} size={12} color={fg} />
      {children}
      {clock != null && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><Icon name="clock" size={10} />{clock}ד׳</span>}
    </span>
  );
}

// ── עזרי פריסה ────────────────────────────────────────────────────────────────
function StepBlock({ n, icon, title, subtitle, children }: { n: number; icon: IconName; title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="stack" style={{ gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'inline-flex', width: 26, height: 26, borderRadius: '50%', background: 'var(--accent)', color: 'var(--on-accent, #fff)', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--t-sm)', fontWeight: 700, flexShrink: 0 }}>{n}</span>
        <Icon name={icon} size={16} color="var(--accent)" />
        <div>
          <div style={{ fontWeight: 600, fontSize: 'var(--t-md)' }}>{title}</div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{subtitle}</div>
        </div>
      </div>
      <div className="stack" style={{ gap: 10, paddingInlineStart: 34 }}>{children}</div>
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>{children}</div>;
}
